import React, { useEffect, useState } from "react";
import { api, money } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { toast } from "sonner";
import { Timer, Receipt, Loader2, FileText } from "lucide-react";

const inputCls = "rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none transition";

export const TMBilling = ({ ticketId, onInvoiced }) => {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [saveDefault, setSaveDefault] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/tickets/${ticketId}/tm-billing`).then((d) => { setData(d); setCfg(d.config); }).catch(() => {});
  }, [ticketId]);

  if (!data || !cfg) return null;

  const saveCfg = async () => {
    setBusy(true);
    try {
      const d = await api.put(`/tickets/${ticketId}/tm-billing`, { ...cfg, save_as_default: saveDefault });
      setData(d); setCfg(d.config);
      toast.success(t("saved"));
    } catch { toast.error(t("error")); }
    finally { setBusy(false); }
  };

  const generate = async (draft) => {
    const msg = draft ? (t("tmConfirmDraft") || "Generate DRAFT invoice for manager review?") : (t("tmConfirmInvoice") || "Generate final invoice?");
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      const inv = await api.post(`/tickets/${ticketId}/tm-invoice${draft ? "?draft=true" : ""}`, {});
      toast.success(`${inv.number} — ${money(inv.total)}${draft ? " (draft)" : ""}`);
      onInvoiced && onInvoiced();
    } catch (e) { toast.error(e?.response?.data?.detail || t("error")); }
    finally { setBusy(false); }
  };

  const num = (v) => parseFloat(v) || 0;

  return (
    <div className="border-t border-slate-200 pt-4 space-y-3" data-testid="tm-billing">
      <div className="flex items-center gap-2">
        <Timer className="w-4 h-4 text-slate-500" />
        <span className="text-xs font-bold uppercase tracking-wide text-slate-500">{t("tmTitle")}</span>
        {cfg.specialty && <span className="text-[10px] uppercase font-semibold rounded px-1.5 py-0.5 bg-slate-100 text-slate-500">{cfg.specialty}</span>}
      </div>
      <p className="text-[11px] text-slate-400 -mt-1">{t("tmHint")}</p>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1">{t("tmLaborMode")}</label>
          <select data-testid="tm-labor-mode" value={cfg.labor_mode} onChange={(e) => setCfg({ ...cfg, labor_mode: e.target.value })} className={inputCls}>
            <option value="rate">{t("tmModeRate")}</option>
            <option value="markup">{t("tmModeMarkup")}</option>
          </select>
        </div>
        {cfg.labor_mode === "rate" ? (
          <div>
            <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1">$/h</label>
            <input data-testid="tm-hourly-rate" type="number" step="0.01" value={cfg.hourly_rate} onChange={(e) => setCfg({ ...cfg, hourly_rate: num(e.target.value) })} className={`${inputCls} w-20 text-right`} />
          </div>
        ) : (
          <div>
            <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1">{t("tmLaborMarkup")} %</label>
            <input data-testid="tm-labor-markup" type="number" step="1" value={cfg.labor_markup_pct} onChange={(e) => setCfg({ ...cfg, labor_markup_pct: num(e.target.value) })} className={`${inputCls} w-20 text-right`} />
          </div>
        )}
        <div>
          <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1">{t("tmVisitFee") || "Visit Fee"} $</label>
          <input data-testid="tm-visit-fee" type="number" step="0.01" value={cfg.visit_fee || 0} onChange={(e) => setCfg({ ...cfg, visit_fee: num(e.target.value) })} className={`${inputCls} w-20 text-right`} />
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1">{t("tmMaterialMarkup")} %</label>
          <input data-testid="tm-material-markup" type="number" step="1" value={cfg.material_markup_pct} onChange={(e) => setCfg({ ...cfg, material_markup_pct: num(e.target.value) })} className={`${inputCls} w-20 text-right`} />
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1">{t("tmMinHours")}</label>
          <input data-testid="tm-min-hours" type="number" step="0.5" value={cfg.min_hours} onChange={(e) => setCfg({ ...cfg, min_hours: num(e.target.value) })} className={`${inputCls} w-16 text-right`} />
        </div>
        <button data-testid="tm-save-cfg" onClick={saveCfg} disabled={busy} className="text-xs font-medium bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg px-3 py-2">{t("save")}</button>
        <label className="flex items-center gap-1.5 text-[11px] text-slate-500 pb-2">
          <input type="checkbox" checked={saveDefault} onChange={(e) => setSaveDefault(e.target.checked)} data-testid="tm-save-default" />
          {t("tmSaveDefault")}
        </label>
      </div>

      {data.visits.length > 0 && (
        <table className="w-full text-sm tabular-nums">
          <thead>
            <tr className="text-[11px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-200">
              <th className="text-left py-1.5">{t("tmVisit")}</th>
              <th className="text-right py-1.5">{t("hours")}</th>
              <th className="text-right py-1.5">{t("tmBillable")}</th>
              <th className="text-right py-1.5">$/h</th>
              <th className="text-right py-1.5">{t("total")}</th>
            </tr>
          </thead>
          <tbody>
            {data.visits.map((v, i) => (
              <tr key={i} className="border-b border-slate-100">
                <td className="py-1.5 text-slate-700">{v.tech_name} <span className="text-xs text-slate-400">{v.date}</span></td>
                <td className="py-1.5 text-right text-slate-500">{v.hours}h</td>
                <td className="py-1.5 text-right text-slate-700">{v.billable_hours}h</td>
                <td className="py-1.5 text-right text-slate-500">{money(v.rate)}</td>
                <td className="py-1.5 text-right font-semibold">{money(v.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
        {data.visit_fee_total > 0 && (
          <div className="bg-slate-50 rounded-lg px-3 py-2"><div className="text-[10px] font-bold uppercase text-slate-400">{t("tmVisitFee") || "Visit Fee"} <span className="text-slate-300">({data.visit_days}d)</span></div><div className="font-bold tabular-nums" data-testid="tm-visit-fee-total">{money(data.visit_fee_total)}</div></div>
        )}
        <div className="bg-slate-50 rounded-lg px-3 py-2"><div className="text-[10px] font-bold uppercase text-slate-400">{t("labor")}</div><div className="font-bold tabular-nums">{money(data.labor_total)}</div></div>
        <div className="bg-slate-50 rounded-lg px-3 py-2"><div className="text-[10px] font-bold uppercase text-slate-400">{t("material")} <span className="text-slate-300">({data.materials.length})</span></div><div className="font-bold tabular-nums">{money(data.material_total)}</div></div>
        <div className="bg-emerald-50 rounded-lg px-3 py-2"><div className="text-[10px] font-bold uppercase text-emerald-600">{t("tmClientTotal")}</div><div className="font-bold text-emerald-700 tabular-nums" data-testid="tm-total">{money(data.total)}</div></div>
      </div>

      <div className="flex flex-wrap gap-2">
        <button data-testid="tm-generate-draft" onClick={() => generate(true)} disabled={busy || data.total <= 0}
          className="inline-flex items-center gap-2 rounded-lg bg-amber-500 text-white text-sm font-medium px-4 py-2 hover:bg-amber-600 disabled:opacity-40 transition-colors">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} {t("tmGenerateDraft") || "Generate Draft Invoice"}
        </button>
        <button data-testid="tm-generate-invoice" onClick={() => generate(false)} disabled={busy || data.total <= 0}
          className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 text-white text-sm font-medium px-4 py-2 hover:bg-emerald-700 disabled:opacity-40 transition-colors">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Receipt className="w-4 h-4" />} {t("tmGenerateInvoice")}
        </button>
      </div>
    </div>
  );
};
